import type { FC, ReactNode } from 'react';

import { InlineError, InputField, Switch } from '@/design-system';
import { BUTTON_GHOST, BUTTON_PRIMARY } from '@/shared/ui-class';
import { SkillFormFields } from './SkillFormFields';

export type SkillCreateValues = {
  slug: string;
  name?: string;
  description: string;
  body: string;
};

export const DEFAULT_BODY = `# Skill body

Describe, in markdown, what the agent should do when this skill applies.

- When to use it
- Inputs the agent needs
- Steps to follow
`;

const FIELD_LABEL = 'text-sm font-medium text-zinc-700 dark:text-zinc-300';
const FIELD_HINT = 'text-xs text-muted';

export type SkillCreateFormProps = {
  values: SkillCreateValues;
  onChange: (values: SkillCreateValues) => void;
  onSubmit: () => void;
  mode?: 'diy' | 'agent';
  submitting?: boolean;
  error?: string | null;
  validationError?: string | null;
  showName?: boolean;
  enabled?: boolean;
  onEnabledChange?: (enabled: boolean) => void;
  editMode?: boolean;
  opaqueInputs?: boolean;
  hideHeader?: boolean;
  onCancel?: () => void;
  bodyLabel?: string;
  bodyRows?: number;
  bodyHint?: ReactNode;
  slugPattern: RegExp;
};

export const SkillCreateForm: FC<SkillCreateFormProps> = ({
  values,
  onChange,
  onSubmit,
  mode = 'diy',
  submitting = false,
  error,
  validationError,
  showName = false,
  enabled,
  onEnabledChange,
  editMode = false,
  opaqueInputs = false,
  hideHeader = false,
  onCancel,
  bodyLabel,
  bodyRows,
  bodyHint,
  slugPattern,
}) => {
  const submitLabel =
    mode === 'agent' ? 'Submit' : editMode ? 'Update skill' : 'Create skill';
  const canSubmit = !submitting;
  const slugDisabled = submitting || editMode;
  const showLiveValidation =
    !error &&
    Boolean(validationError) &&
    (values.slug.trim().length > 0 || values.description.trim().length > 0);

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      {!hideHeader ? (
        <header className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
            {editMode ? 'Edit skill' : 'Create a skill'}
          </h2>
          <p className="text-sm text-muted">
            Describe what it does and when the agent should use it.
          </p>
        </header>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <label htmlFor="skill-create-slug" className={FIELD_LABEL}>
          Slug
        </label>
        <InputField
          id="skill-create-slug"
          type="text"
          value={values.slug}
          onChange={(e) => onChange({ ...values, slug: e.target.value })}
          placeholder="my-skill"
          spellCheck={false}
          autoComplete="off"
          disabled={slugDisabled}
        />
        {editMode ? (
          <p className={FIELD_HINT}>Slug is locked after creation.</p>
        ) : (
          <p className={FIELD_HINT}>
            Lowercase letters, digits, and hyphens — the skill&apos;s identifier.
          </p>
        )}
        {!editMode && values.slug && !slugPattern.test(values.slug.trim()) ? (
          opaqueInputs ? (
            <p className="text-xs text-red-600">Kebab-case, starts with a letter.</p>
          ) : null
        ) : null}
      </div>

      <SkillFormFields
        values={values}
        onChange={(next) =>
          onChange({
            ...values,
            ...(showName ? { name: next.name } : {}),
            description: next.description,
            body: next.body,
          })
        }
        disabled={submitting}
        showName={showName}
        bodyLabel={bodyLabel}
        bodyRows={bodyRows}
        bodyHint={bodyHint}
      />

      {enabled !== undefined && onEnabledChange ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-divider px-3 py-2">
          <div>
            <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
              Enabled
            </p>
            <p className="text-xs text-muted">
              Off = invisible to agents and removed from all user skill sets.
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={onEnabledChange}
            disabled={submitting}
            aria-label="Enable or disable this skill"
          />
        </div>
      ) : null}

      {error ? <InlineError>{error}</InlineError> : null}
      {showLiveValidation ? <InlineError>{validationError}</InlineError> : null}

      <div className="flex items-center justify-end gap-2 pt-1">
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className={BUTTON_GHOST}
          >
            Cancel
          </button>
        ) : null}
        <button type="submit" disabled={!canSubmit} className={BUTTON_PRIMARY}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
};
